/**
 * Cross-Browser Speech Recognition Adapter
 * 
 * Provides a unified API for speech recognition across browsers.
 * 
 * **Browser Support:**
 * - Chrome/Edge: Uses Web Speech API (native)
 * - Firefox/Safari/Brave: Falls back to service-based solutions (configured)
 * 
 * **Fallback Strategy:**
 * 1. Try Web Speech API (Chrome/Edge)
 * 2. Fall back to service-based solution (Deepgram, AssemblyAI, etc.)
 * 3. Graceful degradation with clear messaging
 * 
 * **Usage:**
 * ```typescript
 * const recognizer = createSpeechRecognizer();
 * recognizer.onResult((transcript) => {
 *   // Handle transcript
 * });
 * recognizer.start();
 * ```
 */

import { bus } from '../../core/events.js';
import { ASSEMBLYAI_TOKEN_REQUEST_DEFAULTS } from './config.js';

export interface LanguageMetadata {
    languageCode: string | null;
    languageConfidence: number | null;
}

export interface SpeechRecognizer {
    start(): void;
    stop(): void;
    abort(): void;
    isListening(): boolean;
    onResult(callback: (transcript: string, metadata?: LanguageMetadata) => void): void;
    onStart(callback: () => void): void;
    onEnd(callback: () => void): void;
    onError(callback: (error: unknown) => void): void;
    removeCallbacks(): void;
}

export interface SpeechRecognizerOptions {
    continuous?: boolean;
    autoRestart?: boolean;
    lang?: string;
}

/**
 * Check if we're in a secure context (HTTPS or localhost)
 * Required for Web Speech API and getUserMedia()
 */
function isSecureContext(): boolean {
    if (typeof window === 'undefined') return false;
    return window.isSecureContext ||
        location.protocol === 'https:' ||
        location.hostname === 'localhost' ||
        location.hostname === '127.0.0.1';
}

const TOKEN_URL_ENV_KEY = 'VITE_ASSEMBLYAI_TOKEN_URL';
const DEFAULT_TOKEN_URL = 'http://localhost:8795/';

function resolveAssemblyAITokenUrl(): URL {
    const rawEndpoint = (import.meta.env?.[TOKEN_URL_ENV_KEY] as string | undefined)?.trim() || DEFAULT_TOKEN_URL;

    try {
        const resolved = new URL(rawEndpoint);
        if (!import.meta.env?.[TOKEN_URL_ENV_KEY]) {
            console.warn(`[speech-recognition] ${TOKEN_URL_ENV_KEY} not set. Falling back to ${DEFAULT_TOKEN_URL}`);
        }
        return resolved;
    } catch (error) {
        throw new Error(
            `Invalid ${TOKEN_URL_ENV_KEY} value: ${rawEndpoint}. Expected a full URL (https://...).`
        );
    }
}

async function fetchAssemblyAITokenFromWorker(): Promise<string> {
    const url = resolveAssemblyAITokenUrl();

    if (!url.searchParams.has('expires_in_seconds')) {
        url.searchParams.set('expires_in_seconds', String(ASSEMBLYAI_TOKEN_REQUEST_DEFAULTS.expiresInSeconds));
    }

    if (!url.searchParams.has('max_session_duration_seconds')) {
        url.searchParams.set('max_session_duration_seconds', String(ASSEMBLYAI_TOKEN_REQUEST_DEFAULTS.maxSessionDurationSeconds));
    }

    let response: Response;
    try {
        response = await fetch(url.toString(), { method: 'GET' });
    } catch (networkError) {
        throw new Error(
            `Failed to reach AssemblyAI token endpoint (${url.origin}). ${networkError instanceof Error ? networkError.message : networkError}`
        );
    }

    let payload: any = null;
    try {
        payload = await response.json();
    } catch {
        // Ignore JSON parse errors; handled below
    }

    if (!response.ok) {
        const message = payload?.error || payload?.message || response.statusText;
        throw new Error(`AssemblyAI token endpoint error (${response.status}): ${message}`);
    }

    const token = payload?.token;
    if (!token) {
        throw new Error('AssemblyAI token endpoint did not return a token.');
    }

    return token;
}

/**
 * Create speech recognizer with automatic fallback
 * 
 * Strategy:
 * 1. Default to AssemblyAI for reliable cross-browser support
 * 2. Web Speech API is only used as a fallback option (not enabled by default)
 * 
 * CRITICAL: Requires secure context (HTTPS or localhost)
 * - Works on: localhost (http://localhost), HTTPS sites
 * - Doesn't work on: HTTP sites (except localhost), 127.0.0.1 without HTTPS
 */
export function createSpeechRecognizer(options: SpeechRecognizerOptions = {}): SpeechRecognizer {
    // Check if we're in a secure context
    const secureContext = isSecureContext();

    if (!secureContext) {
        // Fall back to AssemblyAI (which also requires secure context, but will give clearer error)
        return createFallbackRecognizer(options);
    }

    // DEFAULT: Use AssemblyAI for reliable cross-browser support
    // Web Speech API often fails with network errors (especially in Brave, but also in Chrome)
    // AssemblyAI works consistently across all browsers
    return createFallbackRecognizer(options);

    // NOTE: Web Speech API is available but not used by default due to reliability issues
    // If you want to enable Web Speech API, you can add a flag or option here
    // const webSpeechAvailable = checkWebSpeechAPI();
    // if (webSpeechAvailable && options.useWebSpeechAPI) {
    //     return createWebSpeechRecognizer(options);
    // }
}

/**
 * Detect browser for better error messaging and fallback decisions
 */
function detectBrowser(): { name: string; version: string } {
    if (typeof window === 'undefined') {
        return { name: 'Unknown', version: 'Unknown' };
    }

    const ua = navigator.userAgent;

    if (ua.includes('Firefox')) {
        const match = ua.match(/Firefox\/(\d+)/);
        return { name: 'Firefox', version: match ? match[1] : 'Unknown' };
    }

    if (ua.includes('Safari') && !ua.includes('Chrome')) {
        const match = ua.match(/Version\/(\d+)/);
        return { name: 'Safari', version: match ? match[1] : 'Unknown' };
    }

    if (ua.includes('Chrome')) {
        const match = ua.match(/Chrome\/(\d+)/);
        return { name: 'Chrome', version: match ? match[1] : 'Unknown' };
    }

    if (ua.includes('Brave')) {
        return { name: 'Brave', version: 'Unknown' };
    }

    if (ua.includes('Edge')) {
        const match = ua.match(/Edge\/(\d+)/);
        return { name: 'Edge', version: match ? match[1] : 'Unknown' };
    }

    return { name: 'Unknown', version: 'Unknown' };
}

/**
 * Check if Web Speech API is available
 */
function checkWebSpeechAPI(): boolean {
    if (typeof window === 'undefined') return false;

    // Check for Web Speech API
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    return typeof SpeechRecognition !== 'undefined';
}

/**
 * Create Web Speech API recognizer (Chrome/Edge)
 */
function createWebSpeechRecognizer(options: SpeechRecognizerOptions): SpeechRecognizer {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    // Configure recognition
    recognition.continuous = options.continuous ?? true;
    recognition.interimResults = false;
    recognition.lang = options.lang || 'en-US';

    let isListening = false;
    let resultCallbacks: Array<(transcript: string) => void> = [];
    let startCallbacks: Array<() => void> = [];
    let endCallbacks: Array<() => void> = [];
    let errorCallbacks: Array<(error: unknown) => void> = [];

    // Set up event handlers
    recognition.onresult = (event: any) => {
        if (event.results && event.results.length > 0) {
            const transcript = event.results[event.results.length - 1][0].transcript;
            resultCallbacks.forEach(cb => cb(transcript));
        }
    };

    recognition.onstart = () => {
        isListening = true;
        startCallbacks.forEach(cb => cb());
    };

    recognition.onend = () => {
        // Only update state if we're still in listening mode
        // This prevents recursive restart loops
        if (isListening) {
            isListening = false;
            endCallbacks.forEach(cb => cb());

            // Auto-restart if enabled (but only if callbacks haven't been removed)
            // This prevents restart loops when stop() is called
            if (options.autoRestart && resultCallbacks.length > 0) {
                // Wait a bit before restarting to avoid immediate restart loops
                setTimeout(() => {
                    // Double-check we should still restart (callbacks might have been removed)
                    if (options.autoRestart && resultCallbacks.length > 0 && !isListening) {
                        try {
                            recognition.start();
                        } catch (e) {
                            // Already started or error - ignore
                        }
                    }
                }, 100);
            }
        }
    };

    recognition.onerror = (event: any) => {
        const error = event.error || event;
        const errorCode = event.error || event.code || 'unknown';
        const errorMessage = event.message || String(error);

        // Enhanced error logging with context
        const errorDetails = {
            error: error,
            errorCode: errorCode,
            errorMessage: errorMessage,
            isListening: isListening,
            continuous: recognition.continuous,
            lang: recognition.lang,
            secureContext: typeof window !== 'undefined' ? window.isSecureContext : false,
            location: typeof window !== 'undefined' ? window.location.href : 'unknown',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
            timestamp: new Date().toISOString()
        };

        // Map common error codes to helpful messages
        let helpfulMessage = errorMessage;
        switch (errorCode) {
            case 'network':
                helpfulMessage = 'Network error: Web Speech API cannot connect to Google\'s servers. ' +
                    'This often happens in Brave browser or when network is blocked. ' +
                    'Try using AssemblyAI fallback instead.';
                break;
            case 'no-speech':
                helpfulMessage = 'No speech detected: Make sure your microphone is working and not muted.';
                break;
            case 'audio-capture':
                helpfulMessage = 'Audio capture error: Microphone may not be available or permission denied.';
                break;
            case 'aborted':
                helpfulMessage = 'Recognition aborted: This is normal when stopping manually.';
                break;
            case 'not-allowed':
                helpfulMessage = 'Permission denied: Please allow microphone access in your browser settings.';
                break;
            case 'service-not-allowed':
                helpfulMessage = 'Service not allowed: Web Speech API may be disabled in your browser.';
                break;
            default:
                helpfulMessage = `Unknown error (${errorCode}): ${errorMessage}`;
        }

        // Create enhanced error object
        const enhancedError = new Error(helpfulMessage);
        (enhancedError as any).originalError = error;
        (enhancedError as any).errorCode = errorCode;
        (enhancedError as any).details = errorDetails;

        errorCallbacks.forEach(cb => cb(enhancedError));
    };

    return {
        start(): void {
            if (isListening) return;
            try {
                recognition.start();
            } catch (e) {
                // Already started or error
            }
        },

        stop(): void {
            if (!isListening) return;
            recognition.stop();
            isListening = false;
        },

        abort(): void {
            recognition.abort();
            isListening = false;
        },

        isListening(): boolean {
            return isListening;
        },

        onResult(callback: (transcript: string) => void): void {
            resultCallbacks.push(callback);
        },

        onStart(callback: () => void): void {
            startCallbacks.push(callback);
        },

        onEnd(callback: () => void): void {
            endCallbacks.push(callback);
        },

        onError(callback: (error: unknown) => void): void {
            errorCallbacks.push(callback);
        },

        removeCallbacks(): void {
            resultCallbacks = [];
            startCallbacks = [];
            endCallbacks = [];
            errorCallbacks = [];
        }
    };
}

/**
 * Create fallback recognizer using AssemblyAI (for browsers without Web Speech API)
 * 
 * Uses AssemblyAI's real-time WebSocket API for cross-browser speech recognition.
 * This provides speech recognition in Firefox, Safari, Brave, and other browsers
 * that don't support the Web Speech API.
 */
function createFallbackRecognizer(options: SpeechRecognizerOptions): SpeechRecognizer {
    let isListening = false;
    let resultCallbacks: Array<(transcript: string, metadata?: LanguageMetadata) => void> = [];
    let startCallbacks: Array<() => void> = [];
    let endCallbacks: Array<() => void> = [];
    let errorCallbacks: Array<(error: unknown) => void> = [];

    // AssemblyAI WebSocket connection
    let ws: WebSocket | null = null;
    let audioStream: MediaStream | null = null;
    let audioProcessor: { processor: ScriptProcessorNode; audioContext: AudioContext; source: MediaStreamAudioSourceNode } | null = null;
    let sessionId: string | null = null;

    /**
     * Get temporary token from the AssemblyAI token worker.
     * The worker URL is provided via Vite env: VITE_ASSEMBLYAI_TOKEN_URL
     */
    async function getToken(): Promise<string> {
        return fetchAssemblyAITokenFromWorker();
    }

    const start = async (): Promise<void> => {
        if (isListening) return;

        // Check secure context first (required for getUserMedia)
        if (!isSecureContext()) {
            const err = new Error(
                'Speech recognition requires secure context (HTTPS or localhost). ' +
                'Use https:// or http://localhost (not http://127.0.0.1 or other IPs). ' +
                'Current URL: ' + (typeof window !== 'undefined' ? window.location.href : 'unknown')
            );
            errorCallbacks.forEach(cb => cb(err));
            return;
        }

        try {
            // Get temporary token from token server
            // This keeps the API key secure on the server side
            const token = await getToken();

            // Get audio stream from user's microphone
            // Requires secure context (HTTPS or localhost)
            audioStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true
                }
            });

            // Connect to AssemblyAI Universal Streaming WebSocket using temporary token
            // Note: Browsers don't support custom headers in WebSocket constructor
            // AssemblyAI Universal Streaming API uses token as query parameter
            // According to docs: https://www.assemblyai.com/docs/speech-to-text/universal-streaming
            // Required parameters: sample_rate, encoding
            // Token parameter: used for authentication instead of Authorization header
            const connectionParams = new URLSearchParams({
                sample_rate: '16000',
                encoding: 'pcm_s16le', // Required: PCM16 encoding
                format_turns: 'true',
                language_detection: 'true', // Enable Automatic Language Detection (ALD)
                language_confidence_threshold: '0.7', // Optional: confidence threshold
                token: token // Use temporary token (not API key)
            });

            // Use AssemblyAI Universal Streaming API endpoint
            // According to docs: wss://streaming.assemblyai.com/v3/ws
            const wsUrl = `wss://streaming.assemblyai.com/v3/ws?${connectionParams.toString()}`;
            ws = new WebSocket(wsUrl);
            ws.onopen = () => {

                // Ensure audioStream is available before processing
                if (!audioStream) {
                    errorCallbacks.forEach(cb => cb(new Error('Audio stream not available')));
                    stop();
                    return;
                }

                // Use AudioContext with ScriptProcessorNode to convert audio to PCM
                // NOTE: ScriptProcessorNode is deprecated but needed for raw PCM conversion
                // TODO: Migrate to AudioWorklet when time permits (requires separate worklet file)
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
                    sampleRate: 16000
                });

                const source = audioContext.createMediaStreamSource(audioStream);
                const processor = audioContext.createScriptProcessor(4096, 1, 1);

                // Throttle audio level events (limit to ~30fps)
                let lastAudioLevelEmit = 0;
                const audioLevelThrottle = 33; // ~30fps

                processor.onaudioprocess = (event) => {
                    if (!ws || ws.readyState !== WebSocket.OPEN) return;

                    const inputData = event.inputBuffer.getChannelData(0);
                    const pcmData = new Int16Array(inputData.length);

                    // Calculate audio amplitude for waveform visualization
                    let sum = 0;
                    for (let i = 0; i < inputData.length; i++) {
                        const abs = Math.abs(inputData[i]);
                        sum += abs * abs; // Square for RMS
                    }
                    // Use RMS (root mean square) for better amplitude representation
                    const rms = Math.sqrt(sum / inputData.length);
                    // Normalize amplitude to 0-1 range, with some amplification for visibility
                    const amplitude = Math.min(1, rms * 3); // Amplify by 3x for better visibility

                    // Convert float32 (-1 to 1) to int16 (-32768 to 32767)
                    for (let i = 0; i < inputData.length; i++) {
                        const s = Math.max(-1, Math.min(1, inputData[i]));
                        pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                    }

                    // Send raw PCM audio data to AssemblyAI
                    ws.send(pcmData.buffer);

                    // Emit audio level event for waveform visualization
                    // Throttle to ~30fps to avoid overwhelming the event system
                    const now = Date.now();
                    if (now - lastAudioLevelEmit >= audioLevelThrottle && amplitude > 0.01) {
                        lastAudioLevelEmit = now;
                        bus.emit('microphone.audio.level', {
                            amplitude,
                            timestamp: now
                        });
                    }
                };

                source.connect(processor);
                processor.connect(audioContext.destination);

                // Store processor for cleanup
                audioProcessor = { processor, audioContext, source };
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    const msgType = data.type;
                    const timestamp = Date.now();

                    if (msgType === 'Begin') {
                        sessionId = data.id;
                        isListening = true;
                        startCallbacks.forEach(cb => cb());
                    } else if (msgType === 'Turn') {
                        const transcript = data.transcript || '';
                        const turnIsFormatted = data.turn_is_formatted || false;
                        const turnEndTime = data.turn_end_time || null;
                        
                        // Extract language metadata from Assembly AI ALD
                        const languageCode = data.language_code || null;
                        const languageConfidence = data.language_confidence !== undefined 
                            ? parseFloat(data.language_confidence) 
                            : null;
                        
                        const languageMetadata: LanguageMetadata = {
                            languageCode,
                            languageConfidence
                        };
                        
                        if (transcript && turnIsFormatted) {
                            // Only emit formatted/complete transcripts with language metadata
                            resultCallbacks.forEach(cb => cb(transcript, languageMetadata));
                        } else if (transcript && !turnIsFormatted) {
                            // Partial transcripts can also include language metadata
                            // (optional: emit partial transcripts with metadata if needed)
                        }
                    } else if (msgType === 'Termination') {
                        stop();
                    } else {
                    }
                } catch (error) {
                }
            };

            ws.onerror = (error) => {
                errorCallbacks.forEach(cb => cb(error));
                stop();
            };

            ws.onclose = (event) => {
                if (isListening) {
                    stop();
                }
            };

        } catch (error) {
            errorCallbacks.forEach(cb => cb(error));
            stop();
        }
    };

    const stop = (): void => {
        isListening = false;

        // Stop audio processor first
        if (audioProcessor) {
            try {
                audioProcessor.processor.disconnect();
                audioProcessor.source.disconnect();
                audioProcessor.audioContext.close().catch(() => { });
            } catch (error) {
            }
            audioProcessor = null;
        }

        // Close WebSocket
        if (ws) {
            if (ws.readyState === WebSocket.OPEN) {
                // Send termination message
                try {
                    ws.send(JSON.stringify({ type: 'Terminate' }));
                } catch (error) {
                }
            }
            ws.close();
            ws = null;
        }

        // Stop audio stream
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
            audioStream = null;
        }

        sessionId = null;
        endCallbacks.forEach(cb => cb());
    };

    return {
        start(): void {
            start().catch((error) => {
                errorCallbacks.forEach(cb => cb(error));
            });
        },

        stop(): void {
            stop();
        },

        abort(): void {
            stop();
        },

        isListening(): boolean {
            return isListening;
        },

        onResult(callback: (transcript: string) => void): void {
            resultCallbacks.push(callback);
        },

        onStart(callback: () => void): void {
            startCallbacks.push(callback);
        },

        onEnd(callback: () => void): void {
            endCallbacks.push(callback);
        },

        onError(callback: (error: unknown) => void): void {
            errorCallbacks.push(callback);
        },

        removeCallbacks(): void {
            resultCallbacks = [];
            startCallbacks = [];
            endCallbacks = [];
            errorCallbacks = [];
        }
    };
}

